import React, { useState, useEffect, useCallback, useRef } from 'react';
import { sseService } from '../../services/sseService';
import { manualTradingApi, analystReviewsApi, agentSignalsApi } from '../../services/api';
import { MonitorSnapshot, MonitorPosition, DcaGroupCard, BlockedSignal, AnalystReview, AgentSignal } from '../../types/monitor.types';
import MonitorSummary from './components/MonitorSummary';
import PositionCard from './components/PositionCard';
import DcaGroupCardComponent from './components/DcaGroupCard';
import TodaysTrades from './components/TodaysTrades';
import BlockedSignals from './components/BlockedSignals';
import AnalystReviews from './components/AnalystReviews';
import AgentSignals from './components/AgentSignals';
import { getCurrentTradingDay } from '../../utils/marketHours';
import { parseUTC } from '../../utils/formatters';
import './LiveMonitor.css';

const MANUAL_ACCOUNT_ID = '299087495130306062';

function groupPositions(positions: MonitorPosition[]): { singles: MonitorPosition[]; dcaGroups: DcaGroupCard[] } {
  const singles: MonitorPosition[] = [];
  const groupMap = new Map<number, MonitorPosition[]>();

  for (const pos of positions) {
    if (pos.dcaGroupId != null && pos.dcaGroupId > 0) {
      if (!groupMap.has(pos.dcaGroupId)) groupMap.set(pos.dcaGroupId, []);
      groupMap.get(pos.dcaGroupId)!.push(pos);
    } else {
      singles.push(pos);
    }
  }

  const dcaGroups: DcaGroupCard[] = [];
  groupMap.forEach((entries, groupId) => {
    const first = entries[0];
    const totalSize = entries.reduce((s, e) => s + e.size, 0);
    const groupPL = entries.reduce((s, e) => s + e.currentPL, 0);
    const groupRValue = entries.reduce((s, e) => s + e.rValue, 0);
    const avgEntry = totalSize > 0
      ? entries.reduce((s, e) => s + e.entryPrice * e.size, 0) / totalSize
      : first.entryPrice;
    const earliest = entries.reduce((min, e) => e.entryTime < min ? e.entryTime : min, entries[0].entryTime);

    dcaGroups.push({
      groupId,
      instrument: first.instrument,
      direction: first.direction,
      model: first.model.replace('DCA ', ''),
      avgEntryPrice: avgEntry,
      currentPrice: first.currentPrice,
      groupPL,
      groupRValue,
      groupCurrentR: groupRValue > 0 ? groupPL / groupRValue : 0,
      tier: Math.max(...entries.map(e => e.tier)),
      tier1ThresholdR: first.tier1ThresholdR,
      exitMode: first.exitMode ?? 'DynamicStop',
      takeProfitR: first.takeProfitR,
      protectedProfit: Math.max(...entries.map(e => e.protectedProfit)),
      stopLevel: first.stopLevel,
      totalSize,
      duration: first.duration,
      entryTime: earliest,
      dcaEntriesFilled: first.dcaEntriesFilled ?? entries.length,
      dcaMaxEntries: first.dcaMaxEntries ?? 3,
      dcaStatus: first.dcaStatus ?? 'ACTIVE',
      dcaDropR: first.dcaDropR,
      dcaNextTriggerR: first.dcaNextTriggerR,
      dcaHardStopR: first.dcaHardStopR,
      dcaStopProximityR: first.dcaStopProximityR,
      entries,
    });
  });

  return { singles, dcaGroups };
}

const LiveMonitor: React.FC = () => {
  const [snapshot, setSnapshot] = useState<MonitorSnapshot | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [todaysTrades, setTodaysTrades] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'trades' | 'blocked' | 'reviews' | 'agent'>('trades');
  const [reviews, setReviews] = useState<AnalystReview[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [agentSignals, setAgentSignals] = useState<AgentSignal[]>([]);
  const [agentSignalsLoading, setAgentSignalsLoading] = useState(false);
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleMonitorUpdate = useCallback((data: MonitorSnapshot) => {
    setSnapshot(data);
    setIsConnected(true);
  }, []);

  const fetchReviews = useCallback(async () => {
    setReviewsLoading(true);
    try {
      // Pull enough to cover today, then filter client-side to today's trading day.
      const data = await analystReviewsApi.getReviews(200);
      const { start, end } = getCurrentTradingDay();
      const today = data.filter(r => {
        const t = parseUTC(r.triggeredAt);
        return t >= start && t <= end;
      });
      setReviews(today);
    } catch (err) {
      console.error('Failed to fetch analyst reviews:', err);
    } finally {
      setReviewsLoading(false);
    }
  }, []);

  const fetchAgentSignals = useCallback(async () => {
    setAgentSignalsLoading(true);
    try {
      const data = await agentSignalsApi.getSignals(200);
      const { start, end } = getCurrentTradingDay();
      const today = data.filter(s => {
        const t = parseUTC(s.generatedAt);
        return t >= start && t <= end;
      });
      setAgentSignals(today);
    } catch (err) {
      console.error('Failed to fetch agent signals:', err);
    } finally {
      setAgentSignalsLoading(false);
    }
  }, []);

  const fetchTodaysTrades = useCallback(async () => {
    try {
      const allTrades = await manualTradingApi.getTrades();
      const { start, end } = getCurrentTradingDay();
      const closed = allTrades.filter((t: any) => {
        const accountId = t.account_id || t.accountId || '';
        if (accountId === MANUAL_ACCOUNT_ID) return false;
        if (t.status !== 'CLOSED' || t.profit_loss == null) return false;
        const exitTime = t.exit_time;
        if (!exitTime) return false;
        const exitDate = parseUTC(exitTime);
        return exitDate >= start && exitDate <= end;
      });
      // Sort by exit_time descending (most recent first)
      closed.sort((a: any, b: any) => parseUTC(b.exit_time).getTime() - parseUTC(a.exit_time).getTime());
      setTodaysTrades(closed);
    } catch (err) {
      console.error('Failed to fetch trades:', err);
    }
  }, []);

  useEffect(() => {
    sseService.connectMonitorStream(handleMonitorUpdate);
    fetchTodaysTrades();

    // Check connection status periodically + refresh trades every 60s
    checkIntervalRef.current = setInterval(() => {
      setIsConnected(sseService.isMonitorStreamConnected());
    }, 3000);

    const tradesInterval = setInterval(fetchTodaysTrades, 60000);

    return () => {
      sseService.disconnectMonitorStream();
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      clearInterval(tradesInterval);
    };
  }, [handleMonitorUpdate, fetchTodaysTrades]);

  return (
    <div className="live-monitor-page">
      <MonitorSummary
        isConnected={isConnected}
        totalPositions={snapshot?.totalPositions ?? 0}
        totalPL={snapshot?.totalPL ?? 0}
        lastUpdate={snapshot?.timestamp ?? null}
      />

      {!snapshot ? (
        <div className="monitor-loading">
          <p>Waiting for monitor data...</p>
          <p className="monitor-loading-sub">Data updates every ~30 seconds</p>
        </div>
      ) : snapshot.positions.length === 0 ? (
        <div className="monitor-empty">
          <p>No open positions</p>
        </div>
      ) : (
        <div className="position-grid">
          {(() => {
            const { singles, dcaGroups } = groupPositions(snapshot.positions);
            return (
              <>
                {dcaGroups.map(g => (
                  <DcaGroupCardComponent key={`dca-${g.groupId}`} group={g} />
                ))}
                {singles.map(pos => (
                  <PositionCard key={pos.tradeId} position={pos} />
                ))}
              </>
            );
          })()}
        </div>
      )}

      <div className="monitor-tabs">
        <button className={`monitor-tab ${activeTab === 'trades' ? 'tab-active' : ''}`} onClick={() => setActiveTab('trades')}>
          Today's Trades {todaysTrades.length > 0 && <span className="tab-count">{todaysTrades.length}</span>}
        </button>
        <button className={`monitor-tab ${activeTab === 'blocked' ? 'tab-active' : ''}`} onClick={() => setActiveTab('blocked')}>
          Blocked {(snapshot?.blockedSignals?.length ?? 0) > 0 && <span className="tab-count tab-count-blocked">{snapshot?.blockedSignals?.length}</span>}
        </button>
        <button
          className={`monitor-tab ${activeTab === 'reviews' ? 'tab-active' : ''}`}
          onClick={() => {
            setActiveTab('reviews');
            if (reviews.length === 0) fetchReviews();
          }}
        >
          Analyst Reviews {reviews.length > 0 && <span className="tab-count tab-count-reviews">{reviews.length}</span>}
        </button>
        <button
          className={`monitor-tab ${activeTab === 'agent' ? 'tab-active' : ''}`}
          onClick={() => {
            setActiveTab('agent');
            if (agentSignals.length === 0) fetchAgentSignals();
          }}
        >
          Discovery Agent {agentSignals.length > 0 && <span className="tab-count tab-count-agent">{agentSignals.length}</span>}
        </button>
      </div>

      {activeTab === 'trades' && <TodaysTrades trades={todaysTrades} />}
      {activeTab === 'blocked' && <BlockedSignals signals={snapshot?.blockedSignals ?? []} />}
      {activeTab === 'reviews' && <AnalystReviews reviews={reviews} loading={reviewsLoading} onRefresh={fetchReviews} />}
      {activeTab === 'agent' && <AgentSignals signals={agentSignals} loading={agentSignalsLoading} onRefresh={fetchAgentSignals} />}
    </div>
  );
};

export default LiveMonitor;
